import { CalendarDays, FolderKanban } from "lucide-react";
import { formatDate } from "../lib/format";
import type { ClientVaultContext, ClientVaultContextVault } from "../lib/types";

interface ClientVaultEventSelectorProps {
  context: ClientVaultContext | null;
  selectedVault: ClientVaultContextVault | null;
  selectedVaultId: string;
  loading?: boolean;
  error?: string;
  onChange: (vaultId: string) => void;
}

export function ClientVaultEventSelector({
  context,
  selectedVault,
  selectedVaultId,
  loading,
  error,
  onChange,
}: ClientVaultEventSelectorProps) {
  if (loading) {
    return <section className="ivory-card rounded-lg p-5 text-sm font-bold text-ktd-muted">Loading your vaults...</section>;
  }

  if (error) {
    return <section className="ivory-card rounded-lg p-5 text-sm font-bold text-red-800">{error}</section>;
  }

  if (!context?.vaults.length) {
    return (
      <section className="ivory-card rounded-lg p-5 text-sm font-bold text-ktd-muted">
        No Memory Media Vault events are assigned to this account yet.
      </section>
    );
  }

  return (
    <section className="ivory-card rounded-lg p-5">
      <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-center">
        <div className="flex items-start gap-4">
          <span className="grid size-11 shrink-0 place-items-center rounded-lg bg-ktd-black text-ktd-softGold">
            <FolderKanban className="size-5" aria-hidden="true" />
          </span>
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-[#D94A16]">
              {context.client.clientName}
            </p>
            <h2 className="mt-1 text-xl font-extrabold text-ktd-rich">
              {selectedVault?.event.eventName || "Select Event Vault"}
            </h2>
            {selectedVault && (
              <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm font-semibold text-ktd-muted">
                <span>{selectedVault.event.eventType}</span>
                {selectedVault.event.eventDate && (
                  <span className="inline-flex items-center gap-1">
                    <CalendarDays className="size-4" aria-hidden="true" />
                    {formatDate(selectedVault.event.eventDate)}
                  </span>
                )}
                <span className="capitalize">Uploads {selectedVault.event.uploadStatus}</span>
              </p>
            )}
          </div>
        </div>
        <label className="grid min-w-72 gap-2 text-sm font-extrabold text-ktd-rich">
          Event / vault workspace
          <select
            value={selectedVaultId}
            onChange={(event) => onChange(event.target.value)}
            className="form-field focus-ring rounded-lg px-4 py-3 text-sm font-bold"
          >
            {context.vaults.map((vault) => (
              <option key={vault.id} value={vault.id}>
                {vault.event.eventName}
              </option>
            ))}
          </select>
        </label>
      </div>
    </section>
  );
}
